import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAssistants,
  useCreateAssistant,
  useUpdateAssistant,
  useUpdateAssistantPassword,
  useDeleteAssistant,
  useCheckUsernameAvailability,
} from '@/hooks/useAssistants';
import { validatePassword, generateSecurePassword } from '@/lib/password-validation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Pencil, Trash2, Plus, Save, X, User, Shield, Eye, EyeOff, RefreshCw } from 'lucide-react';
import type { Assistant, AssistantPermissions } from '@/types/entities';
import { ROLE_TEMPLATES, EMPTY_PERMISSIONS } from '@/lib/permissions';
import { AssistantLoginLink } from '@/components/assistants/AssistantLoginLink';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AssistantsSectionProps {
  restaurantId: string;
}

export function AssistantsSection({ restaurantId }: AssistantsSectionProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const { data: assistants, isLoading } = useAssistants(restaurantId);
  const createAssistant = useCreateAssistant(restaurantId);
  const updateAssistant = useUpdateAssistant(restaurantId);
  const updateAssistantPassword = useUpdateAssistantPassword();
  const deleteAssistant = useDeleteAssistant(restaurantId);
  const checkUsername = useCheckUsernameAvailability(restaurantId);

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<string[]>([]);
  const [form, setForm] = useState({
    username: '',
    password: '',
    newPassword: '',
    roleName: '',
    permissions: EMPTY_PERMISSIONS,
    isActive: true,
  });

  // Refs to prevent infinite loops
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const checkUsernameMutationRef = useRef(checkUsername);
  const permissionEditorScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  // Update ref when mutation changes
  useEffect(() => {
    checkUsernameMutationRef.current = checkUsername;
  }, [checkUsername]);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  const resetForm = () => {
    setForm({
      username: '',
      password: '',
      newPassword: '',
      roleName: '',
      permissions: EMPTY_PERMISSIONS,
      isActive: true,
    });
    setUsernameError(null);
    setShowPassword(false);
    setShowEditPassword(false);
    setPasswordStrength([]);
  };

  // Check username availability function
  const checkUsernameAvailability = useCallback(
    async (username: string) => {
      if (!username || username.length < 2) {
        setUsernameError(null);
        return;
      }

      setIsCheckingUsername(true);
      try {
        const result = await checkUsernameMutationRef.current.mutateAsync(username);
        if (!result.available) {
          if (result.existsInStaff) {
            setUsernameError(
              t('assistant.usernameExistsInStaff') || 'Username already exists in Staff'
            );
          } else if (result.existsInAssistants) {
            setUsernameError(
              t('assistant.usernameExistsInAssistants') || 'Username already exists in Assistants'
            );
          }
        } else {
          setUsernameError(null);
        }
      } catch (error) {
        console.error('Username check error:', error);
      } finally {
        setIsCheckingUsername(false);
      }
    },
    [t]
  );

  // Debounced username check on input change (only when user stops typing)
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only check if we're in adding mode and username is long enough
    if (form.username.length >= 2 && isAdding) {
      debounceTimerRef.current = setTimeout(() => {
        checkUsernameAvailability(form.username);
      }, 800); // Increased debounce to 800ms to reduce API calls
    } else if (form.username.length === 0) {
      setUsernameError(null);
    }

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [form.username, isAdding, checkUsernameAvailability]);

  // Handle username field blur - check immediately when user leaves the field
  const handleUsernameBlur = useCallback(() => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Check immediately if username is valid length
    if (form.username.length >= 2 && isAdding) {
      checkUsernameAvailability(form.username);
    }
  }, [form.username, isAdding, checkUsernameAvailability]);

  const handleTemplateSelect = (templateKey: string) => {
    if (templateKey === 'custom') {
      setForm({ ...form, permissions: EMPTY_PERMISSIONS });
      return;
    }

    const template = ROLE_TEMPLATES[templateKey as keyof typeof ROLE_TEMPLATES];
    if (template) {
      setForm({
        ...form,
        roleName: isArabic ? template.nameAr : template.nameFr,
        permissions: template.permissions,
      });
    }
  };

  const handlePermissionChange = (
    page: keyof AssistantPermissions,
    action: string,
    checked: boolean
  ) => {
    // Save scroll position before state update
    if (permissionEditorScrollRef.current) {
      scrollPositionRef.current = permissionEditorScrollRef.current.scrollTop;
    }

    // If unchecking "view", also uncheck all other actions for this page
    if (action === 'view' && !checked) {
      const pagePerms = form.permissions[page] as Record<string, boolean>;
      const resetPerms: Record<string, boolean> = {};
      Object.keys(pagePerms).forEach((key) => {
        resetPerms[key] = false;
      });
      setForm({
        ...form,
        permissions: {
          ...form.permissions,
          [page]: resetPerms,
        },
      });
      return;
    }

    setForm({
      ...form,
      permissions: {
        ...form.permissions,
        [page]: {
          ...form.permissions[page],
          [action]: checked,
        },
      },
    });
  };

  // Restore scroll position after form state updates
  useEffect(() => {
    if (permissionEditorScrollRef.current && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (permissionEditorScrollRef.current) {
          permissionEditorScrollRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [form.permissions]);

  // Helper to check if view permission is enabled for a page
  const isViewEnabled = (page: keyof AssistantPermissions): boolean => {
    const pagePerms = form.permissions[page] as Record<string, boolean> | undefined;
    return pagePerms?.['view'] === true;
  };

  const handleAdd = async () => {
    if (!form.username || !form.password || !form.roleName) {
      setAlertDialog({
        open: true,
        title: t('common.error') || 'Error',
        description: t('assistant.fillAllFields') || 'Please fill in all required fields',
      });
      return;
    }

    if (usernameError) {
      return;
    }

    try {
      await createAssistant.mutateAsync({
        username: form.username,
        password: form.password,
        roleName: form.roleName,
        permissions: form.permissions,
      });

      resetForm();
      setIsAdding(false);
    } catch (error: any) {
      console.error('Create assistant error:', error);

      // Check for duplicate key error (username already used, possibly deleted)
      const errorMessage = error?.message || '';
      const errorCode = error?.code || '';
      const isDuplicateKeyError =
        errorCode === '23505' ||
        errorMessage.includes('duplicate key') ||
        errorMessage.includes('already exists');

      setAlertDialog({
        open: true,
        title: t('common.error') || 'Error',
        description: isDuplicateKeyError
          ? t('assistant.usernameAlreadyUsed')
          : t('assistant.createError') || 'Failed to create assistant. Please try again.',
      });
    }
  };

  const handleEdit = (assistant: Assistant) => {
    setEditingId(assistant.id);
    setForm({
      username: assistant.username,
      password: '',
      newPassword: '',
      roleName: assistant.roleName,
      permissions: assistant.permissions,
      isActive: assistant.isActive,
    });
    setPasswordStrength([]);
  };

  const handleEditPasswordChange = (password: string) => {
    setForm({ ...form, newPassword: password });
    if (password) {
      const validation = validatePassword(password);
      setPasswordStrength(validation.errors);
    } else {
      setPasswordStrength([]);
    }
  };

  const handleGenerateEditPassword = () => {
    const newPassword = generateSecurePassword(12);
    setForm({ ...form, newPassword });
    setPasswordStrength([]);
  };

  const handleSave = async (assistantId: string) => {
    if (!form.roleName) {
      setAlertDialog({
        open: true,
        title: t('common.error') || 'Error',
        description: t('assistant.roleNameRequired') || 'Role name is required',
      });
      return;
    }

    // Validate password if provided
    if (form.newPassword) {
      const passwordValidation = validatePassword(form.newPassword);
      if (!passwordValidation.isValid) {
        const errorKey = passwordValidation.errors[0] || 'assistant.invalidPassword';
        setAlertDialog({
          open: true,
          title: t('common.error') || 'Error',
          description: t(errorKey) || 'Invalid password',
        });
        return;
      }
    }

    try {
      // Update assistant details
      await updateAssistant.mutateAsync({
        assistantId,
        roleName: form.roleName,
        permissions: form.permissions,
        isActive: form.isActive,
      });

      // Update password if provided
      if (form.newPassword) {
        await updateAssistantPassword.mutateAsync({
          assistantId,
          newPassword: form.newPassword,
        });
      }

      resetForm();
      setEditingId(null);
    } catch (error) {
      console.error('Update assistant error:', error);
      setAlertDialog({
        open: true,
        title: t('common.error') || 'Error',
        description: t('assistant.updateError') || 'Failed to update assistant. Please try again.',
      });
    }
  };

  const handleCancel = () => {
    resetForm();
    setEditingId(null);
    setIsAdding(false);
  };

  const handleDelete = (assistantId: string) => {
    setConfirmDialog({
      open: true,
      title: t('assistant.confirmDelete') || 'Delete Assistant',
      description:
        t('assistant.confirmDeleteDescription') ||
        'Are you sure you want to delete this assistant? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteAssistant.mutateAsync(assistantId);
        } catch (error: any) {
          setAlertDialog({
            open: true,
            title: t('common.error') || 'Error',
            description:
              error?.message ||
              t('assistant.deleteError') ||
              'Failed to delete assistant. Please try again.',
          });
        }
      },
    });
  };

  // Generate random password
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setForm({ ...form, password });
  };

  // Permission Editor Component
  const PermissionEditor = () => (
    <div
      ref={permissionEditorScrollRef}
      className="space-y-4 max-h-96 overflow-y-auto border rounded-md p-4"
    >
      {/* Dashboard */}
      <div>
        <Label className="font-semibold text-base mb-2 block">
          {t('nav.dashboard') || 'Dashboard'}
        </Label>
        <div className="space-y-2 ml-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.dashboard?.view || false}
              onCheckedChange={(checked) => handlePermissionChange('dashboard', 'view', !!checked)}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.view') || 'View'}
            </Label>
          </div>
        </div>
      </div>

      {/* Cancellations */}
      <div>
        <Label className="font-semibold text-base mb-2 block">
          {t('nav.cancellations') || 'Cancellations'}
        </Label>
        <div className="space-y-2 ml-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.cancellations?.view || false}
              onCheckedChange={(checked) =>
                handlePermissionChange('cancellations', 'view', !!checked)
              }
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.view') || 'View'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.cancellations?.accept_reject || false}
              disabled={!isViewEnabled('cancellations')}
              onCheckedChange={(checked) =>
                handlePermissionChange('cancellations', 'accept_reject', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('cancellations') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.acceptReject') || 'Accept/Reject'}
            </Label>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div>
        <Label className="font-semibold text-base mb-2 block">{t('nav.menu') || 'Menu'}</Label>
        <div className="space-y-2 ml-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.menu?.view || false}
              onCheckedChange={(checked) => handlePermissionChange('menu', 'view', !!checked)}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.view') || 'View'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.menu?.add || false}
              disabled={!isViewEnabled('menu')}
              onCheckedChange={(checked) => handlePermissionChange('menu', 'add', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('menu') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.add') || 'Add'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.menu?.edit || false}
              disabled={!isViewEnabled('menu')}
              onCheckedChange={(checked) => handlePermissionChange('menu', 'edit', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('menu') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.edit') || 'Edit'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.menu?.delete || false}
              disabled={!isViewEnabled('menu')}
              onCheckedChange={(checked) => handlePermissionChange('menu', 'delete', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('menu') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.delete') || 'Delete'}
            </Label>
          </div>
        </div>
      </div>

      {/* Stock/Inventory */}
      <div>
        <Label className="font-semibold text-base mb-2 block">
          {t('nav.inventory') || 'Inventory'}
        </Label>
        <div className="space-y-2 ml-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.view || false}
              onCheckedChange={(checked) => handlePermissionChange('stock', 'view', !!checked)}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.view') || 'View'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.add_receipt || false}
              disabled={!isViewEnabled('stock')}
              onCheckedChange={(checked) =>
                handlePermissionChange('stock', 'add_receipt', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('stock') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.addReceipt') || 'Add Receipt'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.edit_receipt || false}
              disabled={!isViewEnabled('stock')}
              onCheckedChange={(checked) =>
                handlePermissionChange('stock', 'edit_receipt', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('stock') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.editReceipt') || 'Update Receipt'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.delete_receipt || false}
              disabled={!isViewEnabled('stock')}
              onCheckedChange={(checked) =>
                handlePermissionChange('stock', 'delete_receipt', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('stock') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.deleteReceipt') || 'Delete Receipt'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.add_validation || false}
              disabled={!isViewEnabled('stock')}
              onCheckedChange={(checked) =>
                handlePermissionChange('stock', 'add_validation', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('stock') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.addValidation') || 'Add Validation'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.stock?.delete_validation || false}
              disabled={!isViewEnabled('stock')}
              onCheckedChange={(checked) =>
                handlePermissionChange('stock', 'delete_validation', !!checked)
              }
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('stock') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.deleteValidation') || 'Delete Validation'}
            </Label>
          </div>
        </div>
      </div>

      {/* Staff */}
      <div>
        <Label className="font-semibold text-base mb-2 block">{t('nav.staff') || 'Staff'}</Label>
        <div className="space-y-2 ml-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.staff?.view || false}
              onCheckedChange={(checked) => handlePermissionChange('staff', 'view', !!checked)}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.view') || 'View'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.staff?.add || false}
              disabled={!isViewEnabled('staff')}
              onCheckedChange={(checked) => handlePermissionChange('staff', 'add', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('staff') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.add') || 'Add'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.staff?.edit || false}
              disabled={!isViewEnabled('staff')}
              onCheckedChange={(checked) => handlePermissionChange('staff', 'edit', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('staff') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.edit') || 'Edit'}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.staff?.delete || false}
              disabled={!isViewEnabled('staff')}
              onCheckedChange={(checked) => handlePermissionChange('staff', 'delete', !!checked)}
            />
            <Label
              className={`text-sm cursor-pointer ${!isViewEnabled('staff') ? 'text-muted-foreground' : ''}`}
            >
              {t('assistant.permissions.delete') || 'Delete'}
            </Label>
          </div>
        </div>
      </div>

      {/* Accounting */}
      <div>
        <Label className="font-semibold text-base mb-2 block">
          {t('nav.accounting') || 'Accounting'}
        </Label>
        <div className="space-y-2 ml-4">
          {/* Salaries checkbox - enables full access */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.salaries?.view || false}
              onCheckedChange={(checked) => {
                // Enable or disable all salaries permissions at once
                setForm({
                  ...form,
                  permissions: {
                    ...form.permissions,
                    salaries: {
                      view: !!checked,
                      add: !!checked,
                      edit: !!checked,
                      delete: !!checked,
                    },
                  },
                });
              }}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.salaries') || 'Salaries'}
            </Label>
          </div>
          {/* Expenses checkbox - enables full access */}
          <div className="flex items-center gap-2">
            <Checkbox
              checked={form.permissions.expenses?.view || false}
              onCheckedChange={(checked) => {
                // Enable or disable all expenses permissions at once
                setForm({
                  ...form,
                  permissions: {
                    ...form.permissions,
                    expenses: {
                      view: !!checked,
                      add: !!checked,
                      edit: !!checked,
                      delete: !!checked,
                    },
                  },
                });
              }}
            />
            <Label className="text-sm cursor-pointer">
              {t('assistant.permissions.expenses') || 'Expenses'}
            </Label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle>{t('settings.assistants') || 'Assistants'}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('settings.assistantsDescription') ||
                'Manage assistant accounts with custom permissions'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { resetForm(); setIsAdding(true); }} disabled={isAdding}>
            <Plus className="w-4 h-4 me-2" />
            <span className="truncate">{t('settings.addAssistant') || 'Add Assistant'}</span>
          </Button>
        </div>

        {/* Assistant Login Link */}
        <AssistantLoginLink restaurantId={restaurantId} />
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {/* Add Form */}
            {isAdding && (
              <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20">
                      <Plus className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg">
                      {t('settings.addAssistant') || 'Add Assistant'}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {/* Username */}
                    <div className="space-y-2">
                      <Label>
                        {t('assistant.username') || 'Username'}
                        <span className="text-destructive"> *</span>
                      </Label>
                      <Input
                        value={form.username}
                        onChange={(e) => setForm({ ...form, username: e.target.value })}
                        onBlur={handleUsernameBlur}
                        placeholder="john_doe"
                        disabled={isCheckingUsername}
                      />
                      {isCheckingUsername && (
                        <p className="text-sm text-muted-foreground">
                          {t('assistant.checkingUsername') || 'Checking username availability...'}
                        </p>
                      )}
                      {usernameError && !isCheckingUsername && (
                        <p className="text-sm text-destructive">{usernameError}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                      <Label>
                        {t('assistant.password') || 'Password'}
                        <span className="text-destructive"> *</span>
                      </Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <Eye className="w-4 h-4 text-muted-foreground" />
                            )}
                          </button>
                        </div>
                        <Button type="button" variant="outline" onClick={generatePassword}>
                          {t('assistant.generate') || 'Generate'}
                        </Button>
                      </div>
                    </div>

                    {/* Role Template Selector */}
                    <div className="space-y-2">
                      <Label>{t('assistant.roleTemplate') || 'Role Template'}</Label>
                      <Select onValueChange={handleTemplateSelect}>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('assistant.selectTemplate') || 'Select a template'}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager">{isArabic ? 'مدير' : 'Manager'}</SelectItem>
                          <SelectItem value="receptionist">
                            {isArabic ? 'موظف استقبال' : 'Receptionist'}
                          </SelectItem>
                          <SelectItem value="inventory_manager">
                            {isArabic ? 'مدير المخزون' : 'Inventory Manager'}
                          </SelectItem>
                          <SelectItem value="custom">
                            {t('assistant.customRole') || 'Custom Role'}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Role Name */}
                    <div className="space-y-2">
                      <Label>
                        {t('assistant.roleName') || 'Role Name'}
                        <span className="text-destructive"> *</span>
                      </Label>
                      <Input
                        value={form.roleName}
                        onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                        placeholder={
                          isArabic ? 'مدير' : t('assistant.roleNamePlaceholder') || 'Manager'
                        }
                      />
                    </div>

                    {/* Permissions */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        {t('assistant.permissionsLabel') ||
                          t('assistant.permissions') ||
                          'Permissions'}
                      </Label>
                      <PermissionEditor />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={createAssistant.isPending}
                      >
                        <X className="w-4 h-4 me-2" />
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                      <Button
                        onClick={handleAdd}
                        disabled={
                          createAssistant.isPending ||
                          !form.username ||
                          !form.password ||
                          !form.roleName ||
                          !!usernameError
                        }
                      >
                        <Save className="w-4 h-4 sm:me-2" />
                        <span className="hidden sm:inline">{t('common.save') || 'Save'}</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Assistants List */}
            {!assistants || assistants.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                    <User className="w-8 h-8 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground">
                  {t('settings.noAssistants') || 'No assistants yet'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                {assistants.map((assistant) => (
                  <Card key={assistant.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      {editingId === assistant.id ? (
                        <div className="space-y-4">
                          {/* Edit form - similar to add form but without username */}
                          <div className="space-y-2">
                            <Label>
                              {t('assistant.roleName') || 'Role Name'}
                              <span className="text-destructive"> *</span>
                            </Label>
                            <Input
                              value={form.roleName}
                              onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                            />
                          </div>

                          {/* New Password (optional) */}
                          <div className="space-y-2">
                            <Label>
                              {t('staff.newPassword') || 'New Password (optional)'}
                            </Label>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <Input
                                  type={showEditPassword ? 'text' : 'password'}
                                  value={form.newPassword}
                                  onChange={(e) => handleEditPasswordChange(e.target.value)}
                                  placeholder={t('staff.leaveBlankToKeep') || 'Leave blank to keep current'}
                                  disabled={updateAssistant.isPending || updateAssistantPassword.isPending}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowEditPassword(!showEditPassword)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2"
                                >
                                  {showEditPassword ? (
                                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <Eye className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </button>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={handleGenerateEditPassword}
                                disabled={updateAssistant.isPending || updateAssistantPassword.isPending}
                                title={t('assistant.generate') || 'Generate'}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </div>
                            {passwordStrength.length > 0 && (
                              <div className="text-xs text-muted-foreground space-y-1">
                                {passwordStrength.map((error, index) => (
                                  <p key={index} className="text-amber-600">
                                    • {t(error)}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label>
                              {t('assistant.permissionsLabel') ||
                                t('assistant.permissions') ||
                                'Permissions'}
                            </Label>
                            <PermissionEditor />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label>{t('assistant.active') || 'Active'}</Label>
                            <Switch
                              checked={form.isActive}
                              onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
                            />
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(assistant.id)}
                              disabled={updateAssistant.isPending || updateAssistantPassword.isPending || !form.roleName}
                              className="flex-1"
                            >
                              <Save className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.save') || 'Save'}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancel}
                              disabled={updateAssistant.isPending || updateAssistantPassword.isPending}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="font-semibold">{assistant.username}</div>
                                <div className="text-sm text-muted-foreground">
                                  {assistant.roleName}
                                </div>
                              </div>
                            </div>
                            {!assistant.isActive && (
                              <span className="text-xs bg-muted px-2 py-1 rounded">
                                {t('common.inactive') || 'Inactive'}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(assistant)}
                              className="flex-1"
                            >
                              <Pencil className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.edit') || 'Edit'}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(assistant.id)}
                              disabled={deleteAssistant.isPending}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Confirm Dialog */}
      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="destructive"
      />

      {/* Alert Dialog */}
      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
        actionLabel={t('common.ok') || 'OK'}
      />
    </Card>
  );
}
